/**
 * Module: lib/format
 * Role: Pure formatting utilities shared across 3+ screens (rule-of-three).
 *
 * NOTE on L-240: L-240 ("inline-per-screen") governs backend-shape INTERFACES
 * that must match exact API field names — it does NOT govern pure utility
 * functions. A util used in 3+ screens belongs here, imported, not inlined.
 * Do not cite L-240 to keep formatKopecks inline.
 *
 * Used by:
 *   - screens/TasksScreen
 *   - screens/ProfileScreen
 *   - screens/WalletScreen (formatTxType, formatRelativeDate added FTR-1032)
 *   - screens/ProductScreen
 *   - screens/HomeScreen/WalletCard
 */

/**
 * Format a kopecks amount as a ₽ currency string.
 *
 * @param kopecks - integer kopecks value (e.g. 12345 → "123 ₽")
 * @returns Localised RUB string with no fractional digits (e.g. "123 ₽")
 */
export function formatKopecks(kopecks: number): string {
  return (kopecks / 100).toLocaleString('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Map a backend TransactionType StrEnum value to a Russian human label.
 *
 * Backend SSOT: src/shared/enums.py:99 TransactionType (lowercase values).
 * Buyer-facing types are 'credit' (кешбэк) and 'withdraw' (вывод). 'refund' and
 * 'adjustment' may also appear from billing edge cases.
 *
 * @param type - raw TransactionType string from /earnings/history items
 * @returns localized label, or the raw type unchanged if unknown (honest fallback)
 */
export function formatTxType(type: string): string {
  const map: Record<string, string> = {
    credit: 'Кешбэк',
    withdraw: 'Вывод',
    refund: 'Возврат',
    adjustment: 'Корректировка',
  };
  return map[type] ?? type;
}

/**
 * Format an ISO datetime as a relative Russian date for transaction rows.
 * Today / Вчера for fresh items; "5 мая" (day + RU month nominative) for older.
 *
 * Compares calendar day boundaries in the local TZ (not "24h ago" math), so
 * tonight's transaction at 23:59 still shows "Сегодня" tomorrow morning at 00:00
 * — matches iOS Wallet UX. Year is intentionally omitted (history view limit=20
 * never spans years in practice).
 *
 * @param iso - ISO 8601 datetime string (e.g. backend `created_at`)
 * @returns "Сегодня" | "Вчера" | "{d} {month}" where month is RU nominative
 */
export function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  // RU month long is genitive ("мая"), which is exactly what we want for "5 мая".
  const month = d.toLocaleString('ru-RU', { month: 'long' });
  return `${d.getDate()} ${month}`;
}
